import React, { useState, useRef, useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Icon } from "@showitinc/showit-wc";
import cx from "classnames";
import { DROP_AREA_TOP } from "@/constants/LayerConstants";
import { generateDragSpec, generateDropSpec, getTargetArea } from "../../../utils/DnDUtils";
import { SITE_PAGE_ITEM, SITE_BLOCK_ITEM, SITE_PAGE_FOLDER } from "../../../constants/DnDConstants";
import { sitePanelDrop } from "../../../components/sitepanel/SitePanelDropHandlers";
import AppConstants from "../../../constants/AppConstants";
import removeItem from "./SitePanelItem.utils";
import openRowContextMenu from "../../../utils/openRowContextMenu";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import UIActions from "../../../actions/UIActions";
import styles from "./SitePanelItem.module.css";

/**
 * @typedef {import("immutable").Map} Map
 */

/**
 * Pages Panel Item
 * Reusable item component for pages, wordpress templaces, site canvases, and canvas sets
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children - The context menu for the dropdown
 * @param {string} props.id - The id of the item
 * @param {boolean=} props.active - Whether the item is selected
 * @param {Map} props.source - The item object repping the page, block, or canvas set
 * @param {number} props.index - Item index within the total panel
 * @param {number} props.listIndex - Item index with in page/blog item list
 * @param {function} props.onSelect - Callback when the item is selected
 * @returns {React.ReactElement}
 */

const {
  DROP_SHADOW_TOP,
  DROP_SHADOW_BOTTOM,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_BLOCK,
  ITEM_TYPE_CANVAS_SET,
} = AppConstants;

const _renderItemValues = (type) => {
  switch (type) {
    case "page":
    case "wpPage":
      return { DRAG_TYPE: SITE_PAGE_ITEM, ITEM_TYPE: ITEM_TYPE_PAGE };
    case "block":
      return { DRAG_TYPE: SITE_BLOCK_ITEM, ITEM_TYPE: ITEM_TYPE_BLOCK };
    case "canvasSet":
      return { DRAG_TYPE: SITE_BLOCK_ITEM, ITEM_TYPE: ITEM_TYPE_CANVAS_SET };
    default:
      return { DRAG_TYPE: SITE_PAGE_ITEM, ITEM_TYPE: ITEM_TYPE_PAGE };
  }
};

/**
/**
 * Represents an item being dragged.
 * @typedef {Object} DragItem
 * @property {string} id - The unique identifier of the item.
 * @property {number} index - The index of the item in its list.
 * @property {number} listIndex - The index of the list containing the item.
 * @property {string} type - The type of the item, e.g., page, block, or canvas set.
 * @property {boolean} inFolder - Indicates if the item is within a folder.
 * @property {boolean} blog - Indicates if the item is associated with a blog.
 */

/**
 *
 * @param {*} param0
 * @returns
 */

const SitePanelItem = ({
  active,
  children,
  dataIndex,
  id,
  index,
  listIndex,
  measure,
  onSelect,
  source,
  type,
  style = {},
}) => {
  const [dropArea, setDropArea] = useState("");
  const selfRef = useRef();

  const name = source.get("name");
  const slug = source.get("slug");
  const publishable = source.get("publishable", true);
  const isHome = type === "page" ? source.get("home") === 1 : false;
  const isDefault = source.get("default") === 1;

  const inFolder = !!source.get("pId");

  const { DRAG_TYPE, ITEM_TYPE } = useMemo(() => _renderItemValues(type), [type]);
  const blog = !!source.get("wp");

  // prevent folders from being inserted into themselves
  const dropTypes = !inFolder ? [SITE_PAGE_FOLDER, DRAG_TYPE] : DRAG_TYPE;

  const drag = generateDragSpec({
    type: DRAG_TYPE,
    item: {
      id,
      index,
      listIndex,
      type: ITEM_TYPE,
      inFolder,
      blog,
    },
    onDrop: ({ item, target }) =>
      DocumentStore.dispatch({ type: COMMANDS.MOVE_ITEM, payload: [item, target] }),
  });

  const drop = generateDropSpec({
    type: dropTypes,
    canDrop: (item) => blog === item.blog,
    onHover: (monitor) => setDropArea(getTargetArea(monitor, selfRef.current)),
    onDrop: sitePanelDrop({
      id,
      index,
      listIndex,
      dropRef: selfRef.current,
      inFolder,
      type: ITEM_TYPE,
    }),
  });

  /**
   * Alt+right-click deletes a non-active item, so broken/unselectable pages can
   * be removed without first selecting them.
   * @param {React.MouseEvent} e
   */
  const _deleteContentShortcut = (e) => {
    e.preventDefault();
    e.altKey && !active && removeItem({ source, type, name, id, slug });
  };

  /**
   * Right-click selects the item (when not already active) and opens its 3-dot
   * menu. openRowContextMenu defers the open to mouseup so the right-click's own
   * mouseup doesn't immediately dismiss the menu as a click-outside.
   * @param {React.MouseEvent} e
   */
  const _contextMenuShortcut = (e) => {
    openRowContextMenu({
      event: e,
      rowEl: selfRef.current,
      active,
      select: () => onSelect?.(source),
    });
  };

  /**
   * Funnel for right-click events. Routes to the alt-delete shortcut when alt is held,
   * otherwise opens the item's context menu.
   * @param {React.MouseEvent} e
   */
  const _onContextMenu = (e) => {
    if (e.altKey) {
      _deleteContentShortcut(e);
      return;
    }
    _contextMenuShortcut(e);
  };

  const _onClick = (e) => {
    e.preventDefault();
    onSelect && onSelect(source);
  };

  const _rename = (event) => {
    const { target } = event;
    if (target?.closest(".no-autoclose")) {
      return;
    }

    UIActions.toggleRenamePopup({
      title: ITEM_TYPE,
      itemType: ITEM_TYPE,
      itemId: id,
      name,
      slug,
      showWarning: true,
    });
  };

  const [{ isDragging }, dragRef] = useDrag(drag);
  const [{ isOver, canDrop }, dropRef] = useDrop(drop);

  const { ...inlineStyles } = style;

  if (canDrop && isOver && !isDragging) {
    inlineStyles.boxShadow = dropArea === DROP_AREA_TOP ? DROP_SHADOW_TOP : DROP_SHADOW_BOTTOM;
  }

  return (
    <div
      className={cx(styles.root, { [styles.active]: active }, { [styles["in-folder"]]: inFolder })}
      data-index={dataIndex}
      onContextMenu={_onContextMenu}
      onClick={_onClick}
      onDoubleClick={_rename}
      style={inlineStyles}
      ref={(self) => {
        selfRef.current = self;
        dragRef(self);
        dropRef(self);
        measure && measure(self);
      }}
    >
      <div className={cx(styles.item, { [styles.unpublished]: !publishable })}>
        <span className={styles["item-label"]}>{name}</span>
        {isDefault && <span className={cx(styles.icon, styles.star)}>&#9733;</span>}
        {isHome ? <Icon type="home" size="16" className={cx(styles.icon, styles.home)} /> : null}
        {!publishable ? (
          <Icon type="noPublish" size="16" className={cx(styles.icon, styles["no-publish"])} />
        ) : null}
      </div>
      <div className={styles["menu-container"]}>
        <div className={!active ? styles.menu : ""}>{children}</div>
      </div>
    </div>
  );
};

export default SitePanelItem;
